import React from "react";
import type { CompanyBadge } from "reducers/companyProfileApi";
import {
  getBadgePublicAssetUrl,
  getPublicAssetUrl,
} from "components/productSellerCompanyCard/productSellerCompanyCard.utils";
import { LightProfileCard } from "./ProfileCard";

interface CompanyBadgesProps {
  badges: CompanyBadge[];
  isLoading: boolean;
  isError: boolean;
}

const COMPANY_BADGE_TIER_LABELS = {
  bronze: "ブロンズバッジ",
  silver: "シルバーバッジ",
  gold: "ゴールドバッジ",
} as const;

const COMPANY_BADGE_TIER_ORDER = ["bronze", "silver", "gold"] as const;

const groupBadgesByTier = (badges: CompanyBadge[]) =>
  COMPANY_BADGE_TIER_ORDER.reduce(
    (result, tier) => ({
      ...result,
      [tier]: badges.filter((badge) => badge.tier === tier),
    }),
    {} as Record<(typeof COMPANY_BADGE_TIER_ORDER)[number], CompanyBadge[]>,
  );

const getBadgeLabelColor = (badge: CompanyBadge) =>
  badge.is_unlocked
    ? badge.tier === "bronze"
      ? "text-[#9a724f]"
      : badge.tier === "silver"
        ? "text-[#858b9c]"
        : "text-[#b69231]"
    : "text-[#adbbcc]";

const getBadgeIconUrl = (badge: CompanyBadge) =>
  badge.code
    ? getBadgePublicAssetUrl(
        badge.code,
        badge.is_unlocked ? "unlocked" : "locked",
      )
    : badge.icon_url;

const CompanyBadges = ({ badges, isLoading, isError }: CompanyBadgesProps) => {
  const badgesByTier = groupBadgesByTier(badges);

  return (
    <LightProfileCard className="!px-6 !pb-5 !pt-2.5 md:!px-[1.875rem] md:!pb-[1.875rem] md:!pt-5">
      <div className="flex min-h-11 items-center gap-2.5 border-b border-[#d5d5d5] py-2 md:min-h-[3.4375rem] md:gap-[0.9375rem] md:py-2.5">
        <div className="w-1 self-stretch rounded-sm bg-[#1b4b86]" />
        <h2 className="font-['Noto_Sans_JP'] text-lg font-bold leading-[1.6] text-[#1b4b86] md:text-[1.375rem]">
          獲得バッジ一覧
        </h2>
      </div>
      <div className="mt-5 md:mt-[1.875rem]">
        {isLoading && (
          <div className="flex min-h-[15rem] items-center justify-center text-base font-bold text-[#1b4b86]">
            読み込み中...
          </div>
        )}
        {isError && !isLoading && (
          <div className="flex min-h-[15rem] items-center justify-center text-base font-bold text-[#1b4b86]">
            バッジ情報を取得できませんでした
          </div>
        )}
        {!isLoading && !isError && (
          <div className="flex flex-col gap-5 md:gap-[1.875rem]">
            {COMPANY_BADGE_TIER_ORDER.map((tier) => (
              <section key={tier}>
                <h3 className="mb-2.5 text-[0.9375rem] font-bold leading-[1.6] text-[#1b4b86] md:mb-[0.9375rem] md:text-base">
                  {COMPANY_BADGE_TIER_LABELS[tier]}
                </h3>
                <div className="grid grid-cols-2 items-start gap-x-4 gap-y-4 md:flex md:flex-wrap md:gap-x-5 md:gap-y-[1.0625rem]">
                  {badgesByTier[tier].map((badge) => (
                    <div
                      key={badge.code}
                      className="mx-auto flex w-full max-w-[8.125rem] flex-col items-center md:mx-0 md:w-[8.1875rem] md:max-w-none"
                    >
                      <img
                        src={getPublicAssetUrl(getBadgeIconUrl(badge))}
                        alt={badge.name}
                        className="aspect-square w-full max-w-[8.125rem] object-contain md:h-[8.1875rem] md:w-[8.1875rem] md:max-w-none"
                      />
                      <span
                        className={`mt-[0.4375rem] text-center text-xs font-bold leading-[1.4] ${getBadgeLabelColor(
                          badge,
                        )}`}
                      >
                        {badge.name}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </LightProfileCard>
  );
};

export default CompanyBadges;
